import React, { useEffect, useRef } from "react"
import { useParams, Link, Navigate } from "react-router-dom"
import { INSTITUTES, getIcon } from "../constants"
import {
  Plus,
  Target,
  ShieldCheck,
  Activity,
  Binary,
  Microscope,
  ArrowRight,
  Layers,
  MapPin
} from "lucide-react"

const InstituteDetail = () => {
  const { id } = useParams()
  const institute = INSTITUTES.find(i => i.id === id)
  const observerRef = useRef(null)

  useEffect(() => {
    window.scrollTo(0, 0)

    if (institute) {
      document.title = `${institute.name} | VCNR Hospital`
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active")
          }
        })
      },
      { threshold: 0.1 }
    )

    document.querySelectorAll(".reveal").forEach(el => {
      observerRef.current?.observe(el)
    })

    return () => {
      observerRef.current?.disconnect()
      document.title = "VCNR Hospital | World-Class Clinical Excellence"
    }
  }, [id, institute])

  if (!institute) return <Navigate to="/" />

  return (
    <div className="pt-24 pb-20 bg-white">
      {/* Editorial Header */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <div className="order-2 lg:order-1 reveal">
          
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-semibold  mb-12 shimmer-text">
              {institute.name.replace("VCNR ", "")} <br />{" "}
         
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              {institute.description}
            </p>
            <div className="flex flex-wrap gap-4 mb-12 reveal stagger-2">
              <div className="px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl flex items-center gap-3 group">
                <Binary
                  size={16}
                  className="text-vcnr-brand group-hover:rotate-[360deg] transition-transform duration-1000"
                />
                <span className="text-[14px] font-semibold uppercase tracking-widest text-slate-400">
                  Hub ID: {institute.id.toUpperCase()}
                </span>
              </div>
              <div className="px-4 py-2 bg-vcnr-brand/5 border border-vcnr-brand/10 rounded-xl flex items-center gap-3">
                <ShieldCheck
                  size={16}
                  className="text-vcnr-brand animate-pulse"
                />
                <span className="text-[14px] font-semibold uppercase tracking-widest text-vcnr-brand">
                  Tier-1 Governance
                </span>
              </div>
            </div>
            <Link
              to="/booking"
              className="inline-flex items-center px-12 py-6 bg-slate-900 text-white text-[14px] font-semibold   hover:bg-vcnr-brand transition-all shadow-2xl hover:scale-105 active:scale-95 reveal stagger-3 group"
            >
              Schedule Consultation{" "}
              <Plus
                size={14}
                className="ml-4 group-hover:rotate-90 transition-transform"
              />
            </Link>
          </div>
          <div className="order-1 lg:order-2 reveal stagger-2">
            <div className="relative group">
              <div className="scanning-line"></div>
              <div className="absolute inset-0 border-[20px] border-slate-100 translate-x-4 translate-y-4 -z-10 group-hover:translate-x-6 group-hover:translate-y-6 transition-transform"></div>
              <img
                src={institute.image}
                alt={institute.name}
                className="w-full h-[auto] object-cover grayscale brightness-90 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-[1.5s] shadow-2xl"
              />
              <div className="absolute bottom-8 right-8 w-24 h-24 bg-white rounded-3xl flex items-center justify-center text-vcnr-brand shadow-2xl border border-slate-100 animate-float">
                {getIcon(institute.icon)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- REFINED CLINICAL BLUEPRINT SECTION --- */}
      <section className="bg-slate-50 pb-32 relative overflow-hidden border-y border-slate-100">
        <div className="absolute inset-0 hospital-grid opacity-[0.05] pointer-events-none"></div>

<div className="max-w-7xl mx-auto px-6 relative z-10">
  <div className="max-w-3xl mb-24 reveal">
    <div className="inline-flex items-center gap-3 px-3 py-1 bg-white border border-slate-200 rounded-full mb-6">
      <span className="w-1.5 h-1.5 rounded-full bg-vcnr-brand animate-pulse"></span>
      <span className="text-[10px] font-black uppercase text-slate-500">
        Departmental Blueprints
      </span>
    </div>
    <h2 className="text-4xl md:text-[3rem] font-semibold text-slate-900 mb-8 leading-tight">
      Clinical <br />{" "}
      <span className="text-vcnr-brand">
        Unit Boards.
      </span>
    </h2>
    <p className="text-slate-600 text-lg mb-8">
      Every specialized unit within the {institute.name} is governed by
      high-precision clinical protocols and a dedicated specialist
      board.
    </p>
  </div>

  <div className="grid lg:grid-cols-2 gap-12">
    {institute.services.map((service, index) => {
      // Function to get service-specific content
      const getServiceContent = (serviceName) => {
        const serviceLower = serviceName.toLowerCase();
        const instituteLower = institute.name.toLowerCase();
        
        // Institute-specific content
        if (instituteLower.includes('musculo-skeletal') || instituteLower.includes('musculo skeletal')) {
          if (serviceLower.includes('orthopedics')) {
            return {
              description: "Comprehensive bone and joint care including advanced surgical interventions, trauma management, and joint replacement procedures.",
              protocol: "Ortho_Surgical.45",
              unitStatus: "Surgical Core"
            };
          } else if (serviceLower.includes('rheumatology')) {
            return {
              description: "Specialized care for arthritis, autoimmune disorders, and inflammatory joint diseases with biologic therapies.",
              protocol: "Rheuma_Therapy.32",
              unitStatus: "Medical Core"
            };
          } else if (serviceLower.includes('pain')) {
            return {
              description: "Multidisciplinary pain management with interventional procedures, nerve blocks, and rehabilitation protocols.",
              protocol: "Pain_Intervention.28",
              unitStatus: "Interventional"
            };
          } else if (serviceLower.includes('rehabilitation')) {
            return {
              description: "Post-operative and injury rehabilitation with physiotherapy, occupational therapy, and mobility restoration.",
              protocol: "Rehab_Physical.51",
              unitStatus: "Therapeutic"
            };
          } else if (serviceLower.includes('sports')) {
            return {
              description: "Sports injury management, performance optimization, and athlete-specific rehabilitation programs.",
              protocol: "Sports_Performance.39",
              unitStatus: "Performance"
            };
          } else if (serviceLower.includes('ergonomics')) {
            return {
              description: "Workplace injury prevention, posture correction, and ergonomic assessment for occupational health.",
              protocol: "Ergo_Workplace.24",
              unitStatus: "Preventive"
            };
          }
        }
        
        else if (instituteLower.includes('neuro-science') || instituteLower.includes('neuro science')) {
          if (serviceLower.includes('neurology') || serviceLower.includes('mind')) {
            return {
              description: "Comprehensive neurological care for stroke, epilepsy, Parkinson's, and cognitive disorders with advanced diagnostics.",
              protocol: "Neuro_Cognitive.41",
              unitStatus: "Neurological Core"
            };
          } else if (serviceLower.includes('deaddiction')) {
            return {
              description: "Integrated addiction treatment with detoxification, counseling, and relapse prevention programs.",
              protocol: "Addiction_Recovery.33",
              unitStatus: "Behavioral Health"
            };
          } else if (serviceLower.includes('sleep')) {
            return {
              description: "Sleep disorder diagnosis and treatment including polysomnography, CPAP therapy, and sleep hygiene counseling.",
              protocol: "Sleep_Diagnostics.27",
              unitStatus: "Diagnostic"
            };
          } else if (serviceLower.includes('rehabilitation')) {
            return {
              description: "Neurological rehabilitation for stroke recovery, TBI, and neuro-degenerative conditions.",
              protocol: "Neuro_Rehab.48",
              unitStatus: "Rehabilitative"
            };
          }
        }
        
        else if (instituteLower.includes('women-child') || instituteLower.includes('women & child')) {
          if (serviceLower.includes('obstetrics')) {
            return {
              description: "Comprehensive prenatal care, high-risk pregnancy management, and delivery services with fetal monitoring.",
              protocol: "OBGYN_Maternity.52",
              unitStatus: "Maternal Core"
            };
          } else if (serviceLower.includes('gynecology')) {
            return {
              description: "Women's health services including menstrual disorders, menopause management, and gynecological surgeries.",
              protocol: "GYN_Surgical.38",
              unitStatus: "Surgical"
            };
          } else if (serviceLower.includes('pediatrics')) {
            return {
              description: "Comprehensive pediatric care from infancy through adolescence with growth monitoring and vaccination.",
              protocol: "Pediatric_Care.44",
              unitStatus: "Pediatric Core"
            };
          } else if (serviceLower.includes('fertility')) {
            return {
              description: "Advanced fertility treatments including IVF, IUI, and reproductive endocrinology services.",
              protocol: "Fertility_IVF.36",
              unitStatus: "Reproductive"
            };
          } else if (serviceLower.includes('child development')) {
            return {
              description: "Developmental assessment, early intervention, and therapy for children with developmental delays.",
              protocol: "Child_Develop.29",
              unitStatus: "Developmental"
            };
          }
        }
        
        else if (instituteLower.includes('geriatrics')) {
          if (serviceLower.includes('rejuvenation')) {
            return {
              description: "Holistic age management with hormone optimization, nutritional therapy, and vitality restoration.",
              protocol: "Age_Rejuvenate.31",
              unitStatus: "Age Management"
            };
          } else if (serviceLower.includes('ageing')) {
            return {
              description: "Comprehensive geriatric assessment, chronic disease management, and healthy aging programs.",
              protocol: "Geriatric_Care.47",
              unitStatus: "Geriatric Core"
            };
          } else if (serviceLower.includes('rehabilitation')) {
            return {
              description: "Elderly-specific rehabilitation focusing on mobility, balance, and functional independence.",
              protocol: "Senior_Rehab.42",
              unitStatus: "Rehabilitative"
            };
          } else if (serviceLower.includes('home care')) {
            return {
              description: "In-home medical care, nursing services, and telemedicine for elderly patients.",
              protocol: "Home_Medical.25",
              unitStatus: "Domiciliary"
            };
          }
        }
        
        else if (instituteLower.includes('head-neck') || instituteLower.includes('head & neck')) {
          if (serviceLower.includes('ear') || serviceLower.includes('nose') || serviceLower.includes('throat')) {
            return {
              description: "Comprehensive ENT care including sinus surgery, hearing disorders, and voice therapy.",
              protocol: "ENT_Surgical.53",
              unitStatus: "Surgical Core"
            };
          } else if (serviceLower.includes('dental')) {
            return {
              description: "Complete dental care including implants, orthodontics, and oral surgery.",
              protocol: "Dental_Implant.37",
              unitStatus: "Dental"
            };
          } else if (serviceLower.includes('eye')) {
            return {
              description: "Ophthalmology services including cataract surgery, laser vision correction, and retinal care.",
              protocol: "Ophthalmic_Surg.49",
              unitStatus: "Ophthalmic"
            };
          } else if (serviceLower.includes('skin')) {
            return {
              description: "Dermatology and cosmetic procedures including laser therapy and skin cancer screening.",
              protocol: "Derma_Cosmetic.34",
              unitStatus: "Dermatological"
            };
          } else if (serviceLower.includes('hearing') || serviceLower.includes('speech')) {
            return {
              description: "Audiological assessment, hearing aid fitting, and speech therapy services.",
              protocol: "Audio_Speech.26",
              unitStatus: "Therapeutic"
            };
          } else if (serviceLower.includes('swallowing')) {
            return {
              description: "Diagnosis and treatment of swallowing disorders with videofluoroscopy and therapy.",
              protocol: "Swallow_Therapy.30",
              unitStatus: "Specialized"
            };
          }
        }
        
        else if (instituteLower.includes('pulmonology')) {
          if (serviceLower.includes('allergy')) {
            return {
              description: "Allergy testing, immunotherapy, and management of allergic respiratory conditions.",
              protocol: "Allergy_Immuno.28",
              unitStatus: "Immunological"
            };
          } else if (serviceLower.includes('asthma')) {
            return {
              description: "Asthma management with pulmonary function testing, inhalation therapy, and action plans.",
              protocol: "Asthma_Control.35",
              unitStatus: "Respiratory Core"
            };
          } else if (serviceLower.includes('respiratory') || serviceLower.includes('sleep')) {
            return {
              description: "Management of COPD, sleep apnea, and complex respiratory disorders with ventilation support.",
              protocol: "Pulmo_Sleep.46",
              unitStatus: "Critical Respiratory"
            };
          }
        }
        
        else if (instituteLower.includes('gastroenterology')) {
          if (serviceLower.includes('digestive')) {
            return {
              description: "Advanced endoscopy, GI motility studies, and management of digestive disorders.",
              protocol: "GI_Endoscopy.50",
              unitStatus: "GI Core"
            };
          } else if (serviceLower.includes('piles') || serviceLower.includes('fistula')) {
            return {
              description: "Proctology services including laser surgery for hemorrhoids and fistula management.",
              protocol: "Procto_Surgical.40",
              unitStatus: "Surgical"
            };
          } else if (serviceLower.includes('obesity')) {
            return {
              description: "Bariatric surgery, weight management programs, and metabolic syndrome treatment.",
              protocol: "Bariatric_Surg.54",
              unitStatus: "Metabolic Surgical"
            };
          } else if (serviceLower.includes('diet') || serviceLower.includes('nutrition')) {
            return {
              description: "Medical nutrition therapy, dietary counseling, and nutritional assessment.",
              protocol: "Clinical_Nutri.32",
              unitStatus: "Nutritional"
            };
          }
        }
        
        else if (instituteLower.includes('endocrinology')) {
          if (serviceLower.includes('diabetes')) {
            return {
              description: "Comprehensive diabetes care with insulin pumps, continuous glucose monitoring, and education.",
              protocol: "Diabetes_Mgmt.43",
              unitStatus: "Endocrine Core"
            };
          } else if (serviceLower.includes('thyroid')) {
            return {
              description: "Thyroid disorder management including ultrasound, FNAC, and hormone therapy.",
              protocol: "Thyroid_Care.36",
              unitStatus: "Thyroid Specialized"
            };
          } else if (serviceLower.includes('wellness') || serviceLower.includes('lifestyle')) {
            return {
              description: "Preventive health screening, metabolic assessment, and lifestyle modification programs.",
              protocol: "Wellness_Life.29",
              unitStatus: "Preventive Health"
            };
          }
        }
        
        else if (instituteLower.includes('nephrology')) {
          if (serviceLower.includes('nephrology')) {
            return {
              description: "Kidney disease management, dialysis services, and renal transplantation care.",
              protocol: "Renal_Care.55",
              unitStatus: "Renal Core"
            };
          } else if (serviceLower.includes('urology')) {
            return {
              description: "Urological surgery, prostate care, stone management, and male reproductive health.",
              protocol: "Uro_Surgical.48",
              unitStatus: "Urological"
            };
          }
        }
        
        // Default content for any unmatched service
        return {
          description: `Advanced clinical path optimized for ${service.toLowerCase()} featuring real-time monitoring and evidence-based therapeutic resolution.`,
          protocol: "Protocol_V.82",
          unitStatus: "Unit Ready"
        };
      };
      
      const serviceContent = getServiceContent(service);
      
      return (
        <div
          key={index}
          className="relative group bg-white border border-slate-200 p-8 lg:p-12 rounded-[2.5rem] transition-all duration-700 hover:shadow-[0_60px_100px_-30px_rgba(79,70,229,0.15)] hover:-translate-y-2 reveal stagger-${(index % 2) + 1} border-vcnr-brand/30 shadow-[0_20px_50px_-10px_rgba(79,70,229,0.1)]"
        >
          {/* Unit Number Watermark */}
          <div className="absolute top-10 right-10 text-8xl font-black text-slate-50 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity select-none">
            {String(index + 1).padStart(2, "0")}
          </div>

          <div className="flex flex-col md:flex-row gap-10 relative z-10">
            {/* Unit Side Panel */}
            <div className="flex flex-col items-center md:items-start shrink-0">
              <div
                className="w-20 h-20 rounded-[2rem] flex items-center justify-center transition-all duration-700 relative overflow-hidden bg-vcnr-brand text-white shadow-xl shadow-vcnr-brand/30"
              >
                <Target size={32} className="animate-vital-pulse" />
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
              <div className="mt-6 flex flex-col items-center md:items-start gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full bg-vcnr-brand animate-pulse"
                  ></span>
                  <span className="text-[9px] font-semibold uppercase text-slate-400 tracking-widest">
                    {serviceContent.unitStatus}
                  </span>
                </div>
                <div className="text-[9px] font-semibold text-slate-300 uppercase tracking-widest">
                  {serviceContent.protocol}
                </div>
              </div>
            </div>

            {/* Unit Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h3
                  className="text-2xl md:text-3xl font-semibold transition-colors text-slate-900 group-hover:text-vcnr-brand"
                >
                  {service}
                </h3>
              </div>

              <p className="text-slate-600 text-lg mb-8">
                {serviceContent.description}
              </p>

              {/* Action Row */}
              <div className="mt-10 flex items-center justify-between">
                <Link
                  to="/booking"
                  className="flex items-center gap-3 text-[14px] font-semibold text-slate-900 hover:text-vcnr-brand hover:gap-5 transition-all group/btn"
                >
                  Book Slot{" "}
                  <ArrowRight size={14} className="text-vcnr-brand" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
</div>
      </section>

       <div className="bg-slate-900 py-10 px-6">
               <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
                 <div className="flex items-center gap-6">
                   <div className="w-12 h-12 bg-vcnr-brand/20 rounded-xl flex items-center justify-center text-vcnr-brand">
                     <MapPin size={24} />
                   </div>
                   <div>
                     <div className="text-[12px] font-semibold text-slate-400 uppercase tracking-widest mb-1">
                       Facility Address
                     </div>
                     <div className="  text-white uppercase ">
                       Opp Binnamangala Nursery, B.H. Road, Nelamangala - 562123,
                       Bangalore.
                     </div>
                   </div>
                 </div>
                 <div className="flex gap-4">
                   <a
                     href="/booking"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="px-8 py-3 bg-white text-slate-900 rounded-xl text-[12px] font-bold uppercase  hover:bg-vcnr-brand hover:text-white transition-all"
                   >
                     Contact Us
                   </a>
                 </div>
               </div>
             </div>
    </div>
  )
}

export default InstituteDetail
